import { medisApi } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { unwrapMedis, type MedisEnvelope } from '@/lib/medis/envelope';
import { proxifyMedisUrl } from '@/lib/medis/endpointUrls';
import { MEDIS_V2_ROUTES } from '@/lib/medis/v2'; // v1<->v2 switch, see lib/medis/v2/client.ts
import { getResultsV2, getDocumentV2, fetchDocumentBlobV2 } from './results.v2.api'; // v2 when switched on
import { authSession } from '@/features/auth/stores/auth.store';
import type {
  AzureAccessListDto,
  AzureDocumentDto,
  DocumentDownload,
  GetAccessListResponseDto,
  GetDocumentResponseDto,
  MedicalResult,
  ResultDocument,
} from '../types/result.types';

/** Far-past date so `ModifiedSince` returns the full history rather than a delta. */
const EPOCH = '1900-01-01T00:00:00.000Z';

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function mapDocument(dto: AzureDocumentDto): ResultDocument | null {
  if (!dto.DocId) return null;
  const docStatus = dto.DocStatus ?? 'Unknown';
  return {
    medisId: dto.MedisId ?? '',
    docId: dto.DocId,
    docETag: dto.DocETag ?? null,
    docStatus,
    canDownload: docStatus === 'Downloadable',
  };
}

function mapResult(dto: AzureAccessListDto): MedicalResult | null {
  if (!dto.ConsID) return null;
  const documents = (dto.Documents ?? [])
    .map(mapDocument)
    .filter((d): d is ResultDocument => d !== null);
  return {
    consId: dto.ConsID,
    consType: dto.ConsType ?? '',
    consDesc: dto.ConsDesc ?? '',
    typeDesc: dto.TypeDesc ?? dto.ConsType ?? '',
    sampleCode: dto.SampleCode ?? null,
    date: parseDate(dto.Date),
    dateClosed: parseDate(dto.DateClosed),
    isFinal: dto.IsFinal ?? false,
    docCount: dto.DocCount ?? 0,
    docCountAvail: dto.DocCountAvail ?? 0,
    hasAvailableDocuments: (dto.DocCountAvail ?? 0) > 0,
    documents,
    physicianId: dto.PhysicianID ?? null,
    locationId: dto.LocationID ?? null,
    specialityId: dto.SpecialityID ?? null,
  };
}

export const resultsApi = {
  /**
   * Lists a patient's results/consultations.
   * `POST /GetAccessList` with `{ Patient, OnlyWithDoc, ModifiedSince }`.
   * Returns newest first.
   */
  async getResults(patientFhirId: string): Promise<MedicalResult[]> {
    if (MEDIS_V2_ROUTES.getAccessList) return getResultsV2(patientFhirId); // v2 when switched on

    const { data } = await medisApi.post<MedisEnvelope<GetAccessListResponseDto>>(
      endpoints.medis.getAccessList,
      { Patient: patientFhirId, OnlyWithDoc: false, ModifiedSince: EPOCH },
    );
    const rows = unwrapMedis(data)?.AccessListDetails ?? [];
    return rows
      .map(mapResult)
      .filter((r): r is MedicalResult => r !== null)
      .sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0));
  },

  /**
   * Resolves a document's download info.
   * `POST /GetDocument` returns the metadata in the body and the actual
   * download URL in the `Location` response header.
   */
  async getDocument(
    patientFhirId: string,
    consId: string,
    doc: { docId: string; medisId: string; docETag: string | null },
  ): Promise<DocumentDownload> {
    if (MEDIS_V2_ROUTES.getDocument) return getDocumentV2(patientFhirId, consId, doc); // v2 when switched on

    const response = await medisApi.post<MedisEnvelope<GetDocumentResponseDto>>(
      endpoints.medis.getDocument,
      {
        patientId: patientFhirId,
        consID: consId,
        documentId: doc.docId,
        medisId: doc.medisId,
        ...(doc.docETag ? { eTag: doc.docETag } : {}),
      },
      { validateStatus: () => true },
    );

    const inner = response.data?.Response;
    const canDownload = inner?.CanDownload === true;
    const location = (response.headers?.['location'] as string | undefined) ?? null;

    return {
      canDownload,
      downloadUrl: canDownload && location ? proxifyMedisUrl(location) : null,
      documentName: inner?.DocumentName ?? null,
      contentType: inner?.ContentType ?? null,
      status: inner?.Status ?? null,
    };
  },

  /**
   * Fetches the document bytes from its (proxied) download URL.
   *
   * The download URL (from `GetDocument`'s `Location` header) already carries a
   * `DownloadToken`; the only auth it takes is the standard `Authorization:
   * Bearer <jwt>` header, exactly like the mobile app's `downloadDocument` GET.
   *
   * IMPORTANT: do NOT send `x-gral-authorization` here. MEDIS rejects the raw
   * JWT under that header on every endpoint except `/NewSession` (the same
   * reason the MEDIS axios interceptor strips it). This fetch bypasses that
   * interceptor, so the wrong header has to be avoided explicitly.
   *
   * Re-wraps as `application/pdf` so the browser renders it inline in a new tab
   * (the web equivalent of the app's PDF viewer).
   */
  async fetchDocumentBlob(downloadUrl: string, contentType?: string | null): Promise<Blob> {
    if (MEDIS_V2_ROUTES.getDocument) return fetchDocumentBlobV2(downloadUrl, contentType); // v2 when switched on

    const token = authSession.getAccessToken();
    const res = await fetch(downloadUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const buffer = await res.arrayBuffer();
    return new Blob([buffer], { type: contentType || 'application/pdf' });
  },
};
